import React, { useMemo } from 'react';
import { BookOpenText, CaretLeft, CaretRight } from '@phosphor-icons/react';
import { usePreferredBible } from '../../hooks/usePreferredBible';
import type { Verse as VerseType } from '../../types';
import { useBibleStore } from '../../../../shared/store/bibleStore';
import { useAuth } from '../../../../shared/contexts/AuthContext';
import { formatBookName } from '../../utils/bibleBooks';
import { Verse as VerseComponent } from './Verse';
import { getVerseKey } from '../../../../shared/utils/verseUtils';
import { expandVerseRange } from '../../../../shared/utils/verseUtils';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation } from 'swiper/modules';
import 'swiper/css';

interface BibleVersesCarouselProps {
  verses: Record<string, any>;
  onTellMeMore: (verse: { book: string; chapter: string; verse: string }) => void;
  isStreaming?: boolean;
}

export function BibleVersesCarousel({ verses, onTellMeMore, isStreaming = false }: BibleVersesCarouselProps) {
  const { user } = useAuth();

  const expandedVerses = useMemo(() => {
    if (!verses) return [];

    return Object.entries(verses).flatMap(([_, verse]) => {
      if (!verse || typeof verse !== 'object' || !verse.book || !verse.chapter || !verse.verse) {
        return [];
      }

      const verseNumbers = expandVerseRange(verse.verse);

      return verseNumbers.map(verseNumber => ({
        key: getVerseKey({ ...verse, verse: verseNumber }),
        verse: {
          ...verse,
          verse: verseNumber
        }
      }));
    });
  }, [verses]);

  if (expandedVerses.length === 0) {
    return null;
  }

  return (
    <div className="mt-5 text-[#361E06] relative">
      <div className="flex items-center justify-between py-2 mb-5 relative z-30">
        <div className="flex items-center gap-3">
          <BookOpenText className="w-7 h-7" weight="fill" />
          <h5 className="font-medium text-lg">Verse References</h5>  
          <span className="text-sm text-gray-600">({expandedVerses.length} verses)</span>
        </div>
        <div className="flex items-center gap-2">
          <button
            className="custom-prev-button p-1 rounded-full hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <CaretLeft className="w-6 h-6" />
          </button>
          <button
            className="custom-next-button p-1 rounded-full hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <CaretRight className="w-6 h-6" />
          </button>
        </div>
      </div>

      <div className="relative" style={{ zIndex: 1 }}>
        <Swiper
          modules={[Navigation]}
          navigation={{
            nextEl: '.custom-next-button',
            prevEl: '.custom-prev-button',
          }}
          slidesPerView={2}
          spaceBetween={16}
          className="overflow-hidden"
        >
          {expandedVerses.map(({ key, verse }) => (
            <SwiperSlide key={key} className="!h-auto">
              <div className="h-full flex">
                <VerseComponent
                  verse={verse}
                  showReference={true}
                  onTellMeMore={onTellMeMore}
                  className="flex-1 bg-[#E2E0FF] min-h-[300px]"
                />
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
    </div>
  );
}

function LoadingState() {
  return (
    <div className="flex gap-4">
      {[1, 2].map((i) => (
        <div key={i} className="min-w-[40%] max-w-[40%] flex flex-col gap-3">
          <div className="bg-[#F1ECE5] h-[400px] flex-shrink-0 rounded-lg p-4 animate-pulse">
            <div className="h-full flex flex-col">
              <div className="flex-1">
                <div className="w-3/4 h-4 bg-[#E3DCD2] rounded mb-2"></div>
                <div className="w-1/2 h-4 bg-[#E3DCD2] rounded"></div>
              </div>
              <div className="h-6 w-24 bg-[#E3DCD2] rounded"></div>
            </div>
          </div>
          <div className="flex gap-5 px-1">
            {[1, 2, 3].map((j) => (
              <div key={j} className="h-4 w-16 bg-[#E3DCD2] rounded animate-pulse"></div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
} 